import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "wouter";
import {
  Clock,
  Video,
  ArrowLeft,
  ArrowRight,
  Check,
  CalendarDays,
  MessageSquare,
  Search,
  ListChecks,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { SEO } from "@/components/SEO";
import "@/components/marketing/marketing.css";
import {
  bookingWindow,
  bookingTimes,
  calendarDate,
} from "../../../lib/bookingRules.js";
export default function BookingPage() {
  const bounds = useMemo(() => bookingWindow(), []);
  const [date, setDate] = useState<Date>(),
    [time, setTime] = useState(""),
    [step, setStep] = useState<"schedule" | "details" | "done">("schedule"),
    [booked, setBooked] = useState<string[]>([]),
    [loading, setLoading] = useState(false),
    [slotError, setSlotError] = useState(""),
    [retry, setRetry] = useState(0),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [preview, setPreview] = useState(false),
    [confirmed, setConfirmed] = useState(false);
  const lock = useRef(false);
  const bookingMain = useRef<HTMLDivElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (previousStep.current !== step) {
      bookingMain.current?.focus({ preventScroll: true });
      bookingMain.current?.scrollIntoView({ block: "start", behavior: "auto" });
      previousStep.current = step;
    }
  }, [step]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: new URLSearchParams(location.search).get("interest") || "",
    notes: "",
  });
  const selectedLabel = date?.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
  });
  const displayTime = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  };
  useEffect(() => {
    if (!date) return;
    const abort = new AbortController();
    setLoading(true);
    setSlotError("");
    setBooked([]);
    setTime("");
    fetch(`/api/bookings/slots/${calendarDate(date)}`, { signal: abort.signal })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok || !d.success || !Array.isArray(d.bookedTimes))
          throw Error("Availability is unavailable right now. Please retry.");
        setBooked(d.bookedTimes.map((t: string) => t.slice(0, 5)));
        setPreview(Boolean(d.preview));
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setSlotError(e.message || "Could not load availability.");
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [date, retry]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lock.current || !date || !time) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, date: calendarDate(date), time }),
      });
      const d = await r.json();
      if (!r.ok || !d.success || !d.bookingId) {
        if (r.status === 409) {
          setStep("schedule");
          setRetry((x) => x + 1);
        }
        throw Error(
          d.message || "We could not save your booking. Please retry.",
        );
      }
      setPreview(Boolean(d.preview));
      setConfirmed(Boolean(d.emailSent));
      setStep("done");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Unable to book. Please retry.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="growth">
      <SEO
        title="Book a free 15-minute call"
        description="Talk through your business, the work slowing you down, and a practical starting point for AI."
        url="https://www.bettersystems.ai/book"
      />
      <section className="booking-section">
        <div className="wrap">
          <Link href="/" className="back-link">
            <ArrowLeft size={14} />
            Back to the website
          </Link>
          <div className="booking-shell">
            <aside className="booking-aside">
              <div className="host">
                <img src="/rodolfo-portrait.jpg" alt="Rodo Alvarez" />
                <div>
                  <strong>Rodo Alvarez</strong>
                  <span>Founder, Better Systems AI</span>
                </div>
              </div>
              <span className="eyebrow">A clear place to start</span>
              <h1>
                Let’s talk about
                <br />
                your business.
              </h1>
              <p>
                Tell me what you want to improve. We’ll find where AI and
                automation could help.
              </p>
              <div className="meeting-facts">
                <span>
                  <Clock size={16} />
                  15 minutes
                </span>
                <span>
                  <Video size={16} />
                  Online call
                </span>
                <span>
                  <Check size={16} />
                  Free · No obligation
                </span>
              </div>
              <div className="meeting-agenda">
                <strong>We’ll cover</strong>
                <div className="call-roadmap">
                  <div>
                    <span>
                      <MessageSquare size={17} />
                    </span>
                    <p>
                      <strong>Your business</strong>What’s taking your time?
                    </p>
                  </div>
                  <div>
                    <span>
                      <Search size={17} />
                    </span>
                    <p>
                      <strong>The opportunity</strong>Where could AI help?
                    </p>
                  </div>
                  <div>
                    <span>
                      <ListChecks size={17} />
                    </span>
                    <p>
                      <strong>A clear next step</strong>What makes sense to
                      start?
                    </p>
                  </div>
                </div>
              </div>
              {date && (
                <div className="booking-summary">
                  <CalendarDays size={17} />
                  <div>
                    <strong>{selectedLabel}</strong>
                    <span>
                      {time ? `${displayTime(time)} · ` : ""}Phoenix time (MST,
                      UTC−7)
                    </span>
                  </div>
                </div>
              )}
            </aside>
            <div className="booking-main" ref={bookingMain} tabIndex={-1} aria-label="Book your call">
              <div className="booking-steps" aria-label="Booking progress">
                <span className={step === "schedule" ? "active" : ""}>
                  1. Choose a time
                </span>
                <ArrowRight size={13} />
                <span className={step === "details" ? "active" : ""}>
                  2. Your details
                </span>
                <ArrowRight size={13} />
                <span className={step === "done" ? "active" : ""}>3. Done</span>
              </div>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              {step === "schedule" ? (
                <>
                  <h2>Pick a day that works.</h2>
                  <p className="small">
                    Times shown in Phoenix, Arizona (MST, UTC−7).
                  </p>
                  <div className="booking-picker">
                    <div className="booking-calendar">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          setTime("");
                          setError("");
                        }}
                        defaultMonth={new Date(bounds.min + "T12:00:00")}
                        fromDate={new Date(bounds.min + "T00:00:00")}
                        toDate={new Date(bounds.max + "T23:59:59")}
                        disabled={[
                          { before: new Date(bounds.min + "T00:00:00") },
                          { after: new Date(bounds.max + "T23:59:59") },
                          { dayOfWeek: [0, 6] },
                        ]}
                        className="booking-day-picker"
                        showOutsideDays={false}
                      />
                      <p className="small">Weekdays · At least 3 days ahead</p>
                    </div>
                    <div className="booking-times" aria-live="polite">
                      {!date ? (
                        <div className="pick-prompt">
                          <CalendarDays size={28} />
                          <p>
                            Select a date
                            <br />
                            to see times.
                          </p>
                        </div>
                      ) : loading ? (
                        <p className="small">Checking availability…</p>
                      ) : slotError ? (
                        <div>
                          <p className="error">{slotError}</p>
                          <button
                            className="btn secondary"
                            onClick={() => setRetry((x) => x + 1)}
                          >
                            Try again
                          </button>
                        </div>
                      ) : (
                        <>
                          <strong className="small">{selectedLabel}</strong>
                          <div className="time-list">
                            {bookingTimes
                              .filter((t) => !booked.includes(t))
                              .map((t) => (
                                <button
                                  key={t}
                                  className={
                                    time === t
                                      ? "time-slot selected"
                                      : "time-slot"
                                  }
                                  onClick={() => setTime(t)}
                                  aria-pressed={time === t}
                                >
                                  {displayTime(t)}
                                  {time === t && <Check size={13} />}
                                </button>
                              ))}
                            {bookingTimes.every((t) => booked.includes(t)) && (
                              <p className="small">
                                No times left. Choose another day.
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="booking-bottom">
                    <span className="small">
                      {time
                        ? `${selectedLabel} · ${displayTime(time)}`
                        : "Choose a day, then a time."}
                    </span>
                    <button
                      className="btn"
                      disabled={!time || loading || Boolean(slotError)}
                      onClick={() => setStep("details")}
                    >
                      Continue
                      <ArrowRight size={15} />
                    </button>
                  </div>
                  {preview && (
                    <p className="small preview-note">
                      Local preview · Sample availability. No real bookings or
                      emails.
                    </p>
                  )}
                </>
              ) : step === "details" ? (
                <>
                  <button
                    className="back-link"
                    onClick={() => setStep("schedule")}
                  >
                    <ArrowLeft size={14} />
                    Change date or time
                  </button>
                  <h2>A few details, and you’re set.</h2>
                  <form className="form" onSubmit={submit}>
                    <div className="formrow">
                      <label>
                        Your name
                        <input
                          required
                          minLength={2}
                          maxLength={150}
                          autoComplete="name"
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Email
                        <input
                          required
                          type="email"
                          maxLength={150}
                          autoComplete="email"
                          value={form.email}
                          onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                          }
                        />
                      </label>
                    </div>
                    <label>
                      Business name (optional)
                      <input
                        maxLength={150}
                        autoComplete="organization"
                        value={form.company}
                        onChange={(e) =>
                          setForm({ ...form, company: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      What would you like help with?
                      <select
                        value={form.interest}
                        onChange={(e) =>
                          setForm({ ...form, interest: e.target.value })
                        }
                      >
                        <option value="">Not sure yet</option>
                        {Array.from(
                          new Set([
                            "More customers & leads",
                            "Follow-up & email",
                            "Marketing & ads",
                            "Business operations",
                            "Custom app or system",
                            ...(form.interest ? [form.interest] : []),
                          ]),
                        ).map((x) => (
                          <option key={x}>{x}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Anything useful to know? (optional)
                      <textarea
                        rows={3}
                        maxLength={2500}
                        value={form.notes}
                        onChange={(e) =>
                          setForm({ ...form, notes: e.target.value })
                        }
                        placeholder="Tell us a little about your business."
                      />
                    </label>
                    <button className="btn" disabled={busy}>
                      {busy ? "Saving your booking…" : "Book my free call"}
                      <ArrowRight size={15} />
                    </button>
                    <p className="small">
                      We’ll use these details to arrange your call.{" "}
                      <Link href="/privacy">Privacy</Link>.
                    </p>
                  </form>
                </>
              ) : (
                <div className="booking-confirmed">
                  <span className="confirmation-icon">
                    <Check size={26} />
                  </span>
                  <h2>
                    {preview
                      ? "Your preview is complete."
                      : "Your time is reserved."}
                  </h2>
                  <p>
                    {preview
                      ? "No real booking was created and no email was sent."
                      : "We’ve saved your call. Your meeting link will be sent separately."}
                  </p>
                  <div className="confirmed-date">
                    <strong>{selectedLabel}</strong>
                    <span>{displayTime(time)} · Phoenix time (MST, UTC−7)</span>
                    <span>15 minutes · Online</span>
                  </div>
                  {!preview && (
                    <p className="small">
                      {confirmed
                        ? "A confirmation email has been sent."
                        : "Email delivery is not confirmed. Keep these details or contact rodolfo@bettersystems.ai."}
                    </p>
                  )}
                  <Link href="/" className="btn secondary">
                    Back to the website
                    <ArrowRight size={15} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
